"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { useToast } from "@/contexts/ToastContext";
import { selectFaqEntriesForHelp } from "@/lib/faq";
import {
  getProductFeedbackCapture,
  isProductFeedbackEnvEnabled,
  resolveProductFeedbackGate,
  setProductFeedbackAdminEnabled,
  type FeedbackCategory,
} from "@/lib/feedback";
import { openCuoralSupportAsync } from "@/lib/feedback/openCuoralSupport";
import ProductHelpFab from "./ProductHelpFab";
import ProductHelpFaqSheet from "./ProductHelpFaqSheet";
import ProductHelpMenuModal from "./ProductHelpMenuModal";
import ProductFeedbackFormModal from "./ProductFeedbackFormModal";

type Sheet = "none" | "menu" | "feedback" | "faq";

/**
 * Global Help chrome: one FAB → menu (feedback / FAQs / Cuoral / tour).
 * FAQ content from `src/lib/faq`; capture only via `src/lib/feedback`.
 */
export default function ProductHelpChrome() {
  const pathname = usePathname() || "/";
  const { user, isLoading } = useAuth();
  const { openTour, isTourOpen } = useOnboarding();
  const { productFeedbackEnabled } = useSystemSettings();
  const { showToast } = useToast();
  const [sheet, setSheet] = useState<Sheet>("none");
  const [feedbackSession, setFeedbackSession] = useState(0);

  // Keep capture singleton in sync with Admin Settings (env is read live).
  useEffect(() => {
    setProductFeedbackAdminEnabled(productFeedbackEnabled);
  }, [productFeedbackEnabled]);

  // Master = env ∧ Admin Settings; no end-user opt-out (D7/D12).
  const feedbackAllowed = resolveProductFeedbackGate({
    pathname,
    enabled: isProductFeedbackEnvEnabled() && productFeedbackEnabled,
  }).allowed;
  const faqEntries = selectFaqEntriesForHelp(pathname);

  const closeAll = useCallback(() => setSheet("none"), []);

  const handleSendFeedback = useCallback(() => {
    setFeedbackSession((n) => n + 1);
    setSheet("feedback");
  }, []);

  const handleOpenFaqs = useCallback(() => {
    setSheet("faq");
  }, []);

  const handleChatSupport = useCallback(() => {
    setSheet("none");
    void (async () => {
      const opened = await openCuoralSupportAsync();
      if (!opened) {
        showToast(
          "Support chat isn’t ready yet — try again in a moment.",
          "info",
        );
      }
    })();
  }, [showToast]);

  const handleTakeTour = useCallback(() => {
    setSheet("none");
    openTour({
      audience: user ? "auth" : "guest",
      source: "floating_help",
    });
  }, [openTour, user]);

  const onFeedbackShown = useCallback(() => {
    getProductFeedbackCapture().markFeedbackSurveyShown();
  }, []);

  const onFeedbackDismissed = useCallback(() => {
    getProductFeedbackCapture().markFeedbackSurveyDismissed();
  }, []);

  const onFeedbackSubmit = useCallback(
    (input: {
      category: FeedbackCategory;
      primary: string;
      secondary: string;
      anythingElse: string;
    }) => {
      return getProductFeedbackCapture().submitFeedback({
        category: input.category,
        answers: {
          primary: input.primary,
          secondary: input.secondary || undefined,
          anythingElse: input.anythingElse || undefined,
        },
        context: {
          authState: user ? "signed_in" : "anonymous",
          path: pathname,
          surface: "help_fab",
        },
      });
    },
    [pathname, user],
  );

  if (isLoading || isTourOpen) return null;

  const showTour = !pathname.startsWith("/admin");

  return (
    <>
      <ProductHelpFab
        hidden={sheet !== "none"}
        onOpen={() => setSheet("menu")}
      />
      <ProductHelpMenuModal
        isOpen={sheet === "menu"}
        onClose={closeAll}
        feedbackAllowed={feedbackAllowed}
        showTour={showTour}
        onSendFeedback={handleSendFeedback}
        onOpenFaqs={handleOpenFaqs}
        onChatSupport={handleChatSupport}
        onTakeTour={handleTakeTour}
      />
      <ProductHelpFaqSheet
        key={pathname}
        isOpen={sheet === "faq"}
        onClose={closeAll}
        entries={faqEntries}
      />
      <ProductFeedbackFormModal
        key={feedbackSession}
        isOpen={sheet === "feedback"}
        onClose={closeAll}
        onSubmit={onFeedbackSubmit}
        onShown={onFeedbackShown}
        onDismissed={onFeedbackDismissed}
      />
    </>
  );
}
